import { useState, useEffect } from 'react';
import { FaQuoteLeft, FaStar, FaChevronLeft, FaChevronRight, FaPlay, FaPause } from 'react-icons/fa';
import { TESTIMONIAL_CONFIG } from '../../config/testimonialConfig';

const Testimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const testimonials = [
    {
      name: 'Rajesh Sharma',
      position: 'CEO & Founder',
      company: 'TechStart Solutions Pvt Ltd',
      location: 'Mumbai, India',
      imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=200',
      rating: 5,
      text: 'Sutrasync transformed our business with their exceptional web development services. Their understanding of the Indian market and technical expertise helped us achieve 300% growth in online sales within 6 months.',
      project: 'E-commerce Platform Development'
    },
    {
      name: 'Priya Agarwal',
      position: 'Marketing Director',
      company: 'Digital India Solutions',
      location: 'Delhi, India',
      imageUrl: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=200',
      rating: 5,
      text: 'Working with Sutrasync was a game-changer for our digital marketing strategy. They understood our target audience in India perfectly and delivered campaigns that increased our brand visibility by 250%.',
      project: 'Digital Marketing Campaign'
    },
    {
      name: 'Vikram Patel',
      position: 'CTO',
      company: 'Innovation Hub India',
      location: 'Bangalore, India',
      imageUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200',
      rating: 5,
      text: 'The AI and machine learning solutions provided by Sutrasync revolutionized our data processing capabilities. Their team\'s expertise in cutting-edge technology is truly impressive and results-driven.',
      project: 'AI & Machine Learning Implementation'
    },
    {
      name: 'Ananya Singh',
      position: 'Product Manager',
      company: 'Mobile First Technologies',
      location: 'Pune, India',
      imageUrl: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=200',
      rating: 5,
      text: 'Sutrasync developed our mobile app with incredible attention to detail. The user experience is flawless, and our app now has over 100K downloads with 4.8-star ratings on both Android and iOS platforms.',
      project: 'Mobile App Development'
    },
    {
      name: 'Arjun Mehta',
      position: 'Operations Director',
      company: 'Global Enterprises India',
      location: 'Hyderabad, India',
      imageUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=200',
      rating: 5,
      text: 'Their customer support and maintenance services are outstanding. Sutrasync has been our reliable IT partner for 3 years, ensuring 99.9% uptime and seamless operations across all our Indian offices.',
      project: 'IT Support & Maintenance'
    },
    {
      name: 'Kavya Reddy',
      position: 'Founder',
      company: 'StartupIndia Tech',
      location: 'Chennai, India',
      imageUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=200',
      rating: 5,
      text: 'As a startup, we needed cost-effective yet high-quality solutions. Sutrasync delivered beyond expectations with their UI/UX design services, helping us secure ₹2 crore in Series A funding.',
      project: 'UI/UX Design & Branding'
    }
  ];

  // Auto-play functionality with progress tracking
  const [progress, setProgress] = useState(0);
  const { autoSlideInterval, progressUpdateInterval } = TESTIMONIAL_CONFIG;

  useEffect(() => {
    if (!isPaused && TESTIMONIAL_CONFIG.autoStart) {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        setProgress(0); // Reset progress when changing slides
      }, autoSlideInterval);

      return () => clearInterval(interval);
    }
  }, [isPaused, testimonials.length]);

  // Progress bar animation
  useEffect(() => {
    if (!isPaused && TESTIMONIAL_CONFIG.showProgressBar) {
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            return 0;
          }
          return prev + (100 / (autoSlideInterval / progressUpdateInterval));
        });
      }, progressUpdateInterval);

      return () => clearInterval(progressInterval);
    }
  }, [isPaused, currentTestimonial]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    setProgress(0); // Reset progress when manually changing
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setProgress(0); // Reset progress when manually changing
  };

  const goToTestimonial = (index) => {
    setCurrentTestimonial(index);
    setProgress(0); // Reset progress when manually selecting
  };

  const current = testimonials[currentTestimonial];

  return (
    <section className="section-padding bg-white relative font-opensans">
      {/* Decorative BG */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gray-50/30 opacity-50 z-0"></div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#22446b] to-[#338edc] border border-transparent rounded-full text-white text-sm font-medium mb-4">
              <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
              Client Feedback
          </div>
          <h2 className="text-4xl lg:text-5xl font-rajdhani font-bold text-[#22446b] mb-6">
            Client <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#338edc] to-[#22446b]">Testimonials</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            What our Indian clients say about our 'Sutrasync' services. Here's how we've helped 
            businesses across India achieve digital transformation and growth.
          </p>
          
          {/* Slide Counter */}
          {TESTIMONIAL_CONFIG.showSlideCounter && (
            <div className="mt-6 text-center">
              <span className="inline-flex items-center px-4 py-2 bg-gray-100 border border-gray-200 rounded-full text-gray-600 text-sm font-medium">
                <span className="font-bold text-[#22446b]">{currentTestimonial + 1}</span>
                <span className="mx-2">/</span>
                <span>{testimonials.length}</span>
                <span className="ml-2">testimonials</span>
              </span>
            </div>
          )}
        </div>

        {/* Main Testimonial */}
        <div className="max-w-5xl mx-auto" 
             onMouseEnter={() => TESTIMONIAL_CONFIG.pauseOnHover && setIsPaused(true)} 
             onMouseLeave={() => TESTIMONIAL_CONFIG.pauseOnHover && setIsPaused(false)}>
          <div className="bg-gradient-to-br from-[#22446b] via-[#2d5a8a] to-[#338edc] rounded-[2.5rem] p-10 lg:p-14 shadow-2xl relative text-white transition-all duration-500">
            
            {/* Progress Bar */}
            {TESTIMONIAL_CONFIG.showProgressBar && (
              <div className="absolute top-0 left-0 w-full h-1 bg-white/10 rounded-t-[2.5rem] overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-white to-gray-300 transition-all duration-75 ease-linear"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            )}

            {/* Auto-slide indicator with play/pause */}
            {TESTIMONIAL_CONFIG.showPlayPauseButton && (
              <div className="absolute top-6 right-6 flex items-center space-x-3">
                <button
                  onClick={() => setIsPaused(!isPaused)}
                  className="w-8 h-8 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300"
                >
                  {isPaused ? (
                    <FaPlay className="text-white text-xs ml-0.5" />
                  ) : (
                    <FaPause className="text-white text-xs" />
                  )}
                </button>
                <div className="flex items-center space-x-2 text-white/60 text-sm">
                  <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-white/40' : 'bg-white animate-pulse'}`}></div>
                  <span className="font-medium">{isPaused ? 'Paused' : 'Auto'}</span>
                </div>
              </div>
            )}
            {/* Quote Icon */}
            <div className="absolute top-10 left-10 w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg border border-white/10">
              <FaQuoteLeft className="text-2xl text-white" />
            </div>

            <div className="pt-8 lg:px-12 transition-all duration-500 ease-in-out">
              {/* Rating */}
              <div className="flex items-center justify-center mb-6">
                {[...Array(current.rating)].map((_, i) => (
                  <FaStar key={i} className="text-gray-300 text-xl mx-1" />
                ))}
              </div>

              {/* Testimonial Text */}
              <blockquote className="text-xl lg:text-3xl text-white leading-relaxed text-center mb-10 font-rajdhani font-semibold">
                "{current.text}"
              </blockquote>

              {/* Project */}
              <div className="text-center mb-10">
                <span className="inline-block px-5 py-2 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-bold border border-white/20">
                  Project: {current.project}
                </span>
              </div>

              <div className="border-t border-white/10 my-8"></div>

              {/* Client Info */}
              <div className="flex flex-col md:flex-row items-center justify-between">
                 <div className="flex items-center space-x-6 mb-6 md:mb-0">
                    {/* Avatar */}
                    <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full p-1 shadow-lg overflow-hidden group-hover:scale-105 transition-transform duration-300">
                       <img 
                          src={current.imageUrl} 
                          alt={current.name} 
                          className="w-full h-full object-cover rounded-full"
                       />
                    </div>
                    
                    {/* Details */}
                    <div className="text-left">
                      <h4 className="font-rajdhani font-bold text-white text-xl">{current.name}</h4>
                      <p className="text-white/90 font-semibold">{current.position}</p>
                      <p className="text-white/60 text-sm">{current.company} - {current.location}</p>
                    </div>
                 </div>

                 {/* Navigation Buttons */}
                 <div className="flex items-center space-x-4">
                    <button 
                      onClick={prevTestimonial}
                      className="w-14 h-14 bg-white/10 backdrop-blur-sm border-2 border-white/20 hover:bg-white text-white hover:text-[#338edc] rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg">
                      <FaChevronLeft className="text-lg" />
                    </button>
                    <button 
                      onClick={nextTestimonial}
                      className="w-14 h-14 bg-white text-[#338edc] rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105">
                      <FaChevronRight className="text-lg" />
                    </button>
                 </div>
              </div>
            </div>
          </div>

          {/* Slide Indicators */}
          {TESTIMONIAL_CONFIG.showSlideIndicators && (
            <div className="flex justify-center mt-8 space-x-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`relative overflow-hidden rounded-full transition-all duration-300 ${
                    index === currentTestimonial 
                      ? 'w-12 h-3 bg-[#338edc]' 
                      : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                  }`}
                >
                  {index === currentTestimonial && TESTIMONIAL_CONFIG.showProgressBar && (
                    <div 
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#22446b] to-[#338edc] transition-all duration-75 ease-linear"
                      style={{ width: `${progress}%` }}
                    ></div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Thumbs Grid */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className={`p-6 rounded-2xl border transition-all duration-300 cursor-pointer group ${
                index === currentTestimonial 
                  ? 'border-[#338edc] bg-white shadow-lg scale-[1.02]' 
                  : 'border-white bg-gray-50 hover:bg-white hover:border-gray-200 hover:shadow-md'
              }`}
              onClick={() => goToTestimonial(index)}
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden border-2 ${
                    index === currentTestimonial 
                    ? 'border-[#338edc]' 
                    : 'border-transparent group-hover:border-[#338edc] transition-colors'
                }`}>
                  <img 
                      src={testimonial.imageUrl} 
                      alt={testimonial.name}
                      className="w-full h-full object-cover" 
                  />
                </div>
                <div>
                  <h5 className={`font-rajdhani font-bold text-lg ${index === currentTestimonial ? 'text-[#22446b]' : 'text-gray-700'}`}>{testimonial.name}</h5>
                  <p className="text-xs text-gray-500">{testimonial.company}</p>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 italic">
                "{testimonial.text}"
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;